import { HOME } from './home';
import type { WeatherHourly, WeatherNow } from './types';
import { weatherSummary } from './weather';

interface OpenMeteoResponse {
  current?: {
    temperature_2m?: number;
    apparent_temperature?: number;
    relative_humidity_2m?: number;
    weather_code?: number;
    wind_speed_10m?: number;
    uv_index?: number;
  };
  daily?: {
    sunrise?: string[];
    sunset?: string[];
    temperature_2m_max?: number[];
    temperature_2m_min?: number[];
  };
  hourly?: {
    time?: string[];
    temperature_2m?: number[];
    precipitation?: number[];
    weather_code?: number[];
  };
}

export async function fetchOpenMeteo(lat: number, lng: number): Promise<WeatherNow> {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    timezone: HOME.timezone,
    current: 'temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,uv_index',
    hourly: 'temperature_2m,precipitation,weather_code',
    daily: 'sunrise,sunset,temperature_2m_max,temperature_2m_min',
    forecast_days: '2',
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
  if (!res.ok) throw new Error(`Open-Meteo ${res.status}`);
  const body = (await res.json()) as OpenMeteoResponse;
  return parseOpenMeteo(body);
}

export function parseOpenMeteo(body: OpenMeteoResponse): WeatherNow {
  const current = body.current ?? {};
  const now = new Date();
  const times = body.hourly?.time ?? [];
  const start = times.findIndex((t) => new Date(t).getTime() >= now.getTime() - 30 * 60 * 1000);
  const from = start >= 0 ? start : 0;
  const hourly: WeatherHourly[] = times.slice(from, from + 12).map((time, i) => ({
    time,
    tempC: body.hourly?.temperature_2m?.[from + i] ?? 0,
    precipMm: body.hourly?.precipitation?.[from + i] ?? 0,
    weatherCode: body.hourly?.weather_code?.[from + i] ?? 0,
  }));
  const code = current.weather_code ?? 0;
  const highC = body.daily?.temperature_2m_max?.[0];
  const lowC = body.daily?.temperature_2m_min?.[0];
  return {
    locationLabel: 'Carvoeiro, Algarve',
    tempC: current.temperature_2m ?? 0,
    feelsLikeC: current.apparent_temperature ?? current.temperature_2m ?? 0,
    highC: highC ?? current.temperature_2m ?? 0,
    lowC: lowC ?? current.temperature_2m ?? 0,
    weatherCode: code,
    summary: weatherSummary(code),
    humidity: current.relative_humidity_2m ?? 0,
    windKmh: current.wind_speed_10m ?? 0,
    uvIndex: current.uv_index ?? 0,
    sunrise: body.daily?.sunrise?.[0] ?? '',
    sunset: body.daily?.sunset?.[0] ?? '',
    hourly,
  };
}

/** Realistic September Carvoeiro card when both Worker and Open-Meteo are unreachable. */
export function fallbackWeather(): WeatherNow {
  const now = Date.now();
  const hourly: WeatherHourly[] = Array.from({ length: 8 }, (_, i) => ({
    time: new Date(now + i * 60 * 60 * 1000).toISOString(),
    tempC: 25 - (i % 4) * 0.6,
    precipMm: 0,
    weatherCode: 1,
  }));
  return {
    locationLabel: 'Carvoeiro, Algarve',
    tempC: 25,
    feelsLikeC: 26,
    highC: 28,
    lowC: 19,
    weatherCode: 1,
    summary: 'Mostly clear',
    humidity: 58,
    windKmh: 14,
    uvIndex: 7,
    // Real sunrise/sunset come from Open-Meteo. Stamping "now" here made both
    // read as the phone's current clock (a Zulu instant formatted in the device zone).
    sunrise: '',
    sunset: '',
    hourly,
  };
}
